import axios from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faChevronLeft,
    faRoute,
    faStar,
    faClock,
    faCircleCheck,
    faCircleXmark,
    faCircleExclamation,
    faCircleMinus,
    faShieldHalved,
    faUser,
    faPhone,
    faCar,
    faCreditCard,
    faIdCard,
    faFileUpload,
    faCheck,
    faTimes,
    faIndianRupeeSign,
    faBan,
    faLayerGroup,
    faUpRightFromSquare,
    faLocationCrosshairs,
} from '@fortawesome/free-solid-svg-icons'
import styles from './details.module.css'
import MessageDisplay from '../../../components/message/MessageDisplay'
import { useAuth } from '../../../utilities/auth/auth'
import { faStripe } from '@fortawesome/free-brands-svg-icons'
import { groupByDay, groupByMonth } from '../../../utilities/GraphData'
import Bar from '../../../components/bar/Bar'
import Insights from '../../../components/insight/Insight'

const STATUS_CONFIG = {
    Verified: { icon: faCircleCheck, className: 'verified', label: 'Verified' },
    Submitted: { icon: faCircleExclamation, className: 'submitted', label: 'Pending review' },
    Rejected: { icon: faCircleXmark, className: 'rejected', label: 'Rejected' },
    default: { icon: faCircleMinus, className: 'notsubmitted', label: 'Not submitted' },
}

const getStatus = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.default
const isPdf = (docData) => docData?.file_type === 'application/pdf'

const RING_CLASS = {
    verified: 'ringVerified',
    notsubmitted: 'ringNotsubmitted',
}

const documentTypes = [
    { key: 'passport', label: 'Passport', icon: faUser },
    { key: 'driver_kyc', label: 'Driver KYC', icon: faIdCard },
    { key: 'epic', label: 'EPIC', icon: faIdCard },
    { key: 'aadhaar', label: 'Aadhaar', icon: faIdCard },
    { key: 'driving_license', label: 'Driving License', icon: faCar },
    { key: 'vehicle_permit', label: 'Vehicle Permit', icon: faCar },
    { key: 'bank', label: 'Bank Document', icon: faCreditCard },
]

const infoRows = [
    { key: 'driver_contact', label: 'Contact', icon: faPhone },
    { key: 'cab_id', label: 'Cab ID', icon: faIdCard },
    { key: 'cab_number', label: 'Car number', icon: faCar },
    { key: 'operation_service_area', label: 'Area', icon: faLocationCrosshairs },
    { key: 'driver_upi', label: 'UPI ID', icon: faCreditCard },
    { key: 'referral_code', label: 'Refer Code', icon: faLayerGroup },
    { key: 'referred_by', label: 'Referred by', icon: faLayerGroup },
]

const KYC_LABEL_OVERRIDE = {
    driver_kyc: 'Driver KYC',
    aadhaar: 'Driver KYC',
    epic: 'Driver KYC',
}

const KYC_PRIORITY = { driver_kyc: 0, aadhaar: 1, epic: 2 }
const getDisplayLabel = (doc) => KYC_LABEL_OVERRIDE[doc.key] || doc.label
const getKycPriority = (key) => KYC_PRIORITY[key] ?? 99

const DriverDetails = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { id: driver_id } = useParams()

    const [imagePreview] = useState({
        driver_kyc: null,
        passport: null,
        driving_license: null,
        vehicle_permit: null,
        bank: null,
    })
    const [message, setMessage] = useState(null)
    const [details, setDetails] = useState(null)
    const [pfp, setPfp] = useState(null)
    const [loading, setLoading] = useState(true)

    const [preview, setPreview] = useState(null)
    const [previewVisible, setPreviewVisible] = useState(false)
    const [switchCon, setSwitchCon] = useState('Stats')

    const handleNavigate = () => navigate(-1)

    const fetchDetails = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_URL}/api/drivers/details/${driver_id}`,
                { withCredentials: true }
            )
            if (response.data.driverDetail) {
                const driverDetail = response.data.driverDetail
                setDetails(driverDetail)
                setPfp(driverDetail?.document_paths?.passport?.signed_url)
            }
        } catch (error) {
            console.log('Error fetching driver details:', error)
        } finally {
            setLoading(false)   // page renders as soon as this resolves
        }
    }

    useEffect(() => {
        fetchDetails()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // separate effect: fire stats fetch once cab_id is known, independent of page skeleton
    useEffect(() => {
        const cabId = details?.driver?.cab_id
        if (!cabId || statsFetchedRef.current) return

        statsFetchedRef.current = true
        setStatsLoading(true)
        axios.get(`${process.env.REACT_APP_URL}/api/drivers/tripsData/${cabId}`, { withCredentials: true })
            .then((res) => setStats(groupByDay(res.data.stats)))
            .catch((err) => {
                console.log(err, 'error fetching trip stats')
                statsFetchedRef.current = false
            })
            .finally(() => setStatsLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [details?.driver?.cab_id])


    useEffect(() => {
        if (!preview) return
        const onKey = (e) => {
            if (e.key === 'Escape') closePreview()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preview])

    const openPreview = (data) => {
        setPreview(data)
        requestAnimationFrame(() => {
            requestAnimationFrame(() => setPreviewVisible(true))
        })
    }

    const closePreview = () => {
        setPreviewVisible(false)
        setTimeout(() => setPreview(null), 200)
    }

    const handleVerificationAction = async (action, docName) => {
        const data = {
            verification: action,
            token: details?.driver?.fcm_token,
            cab_id: details?.cabs?.cab_id,
        }
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_URL}/api/drivers/Verify/${driver_id}/${docName}`,
                { data },
                { withCredentials: true }
            )

            if (response.data.message === 'success') {
                setDetails((prevDetails) => ({
                    ...prevDetails,
                    document_paths: {
                        ...prevDetails.document_paths,
                        [docName]: {
                            ...prevDetails.document_paths[docName],
                            verification: action,
                        },
                    },
                }))
            }
        } catch (error) {
            setMessage({
                type: 'error',
                message: error.response?.data?.message || 'Verification update failed',
            })
        }
    }

    const handleVerification = async () => {
        if (!details?.driver?.cab_id) {
            return setMessage({ type: 'error', message: 'Cab ID is not available' })
        }

        const ver = details?.driver?.verification ? 0 : 1
        const data = {
            driver_ID: details?.driver?.driver_ID,
            verification: ver,
            cab_id: details?.driver?.cab_id,
            token: details?.driver?.fcm_token,
        }
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_URL}/api/drivers/verifyDriver`,
                data,
                { withCredentials: true }
            )
            if (response.status === 200) {
                setMessage({ type: 'success', message: response.data.message })
                setDetails((prevDetails) => ({
                    ...prevDetails,
                    driver: {
                        ...prevDetails.driver,
                        verification: ver,
                    },
                }))
            }
        } catch (error) {
            setMessage({
                type: 'error',
                message: error.response?.data?.message || 'Action failed',
            })
        }
    }

    const handleNav = () => {
        navigate(`/rides/daily`, { state: { driverId: driver_id } })
    }

    const isReadWrite = user?.permission === 'read_write'
    const verifyLabel = details?.driver?.verification ? 'Unverify' : 'Verify'
    const driverStatus = getStatus(details?.driver?.verification ? 'Verified' : null)
    const ringClass = styles[RING_CLASS[driverStatus.className]] || ''

    const KYC_KEYS = ['driver_kyc', 'aadhaar', 'epic']
    const sortedDocumentTypes = useMemo(() => {
        const passport = documentTypes.find((d) => d.key === 'passport')
        const kycDocs = documentTypes.filter((d) => KYC_KEYS.includes(d.key))
        const others = documentTypes.filter((d) => d.key !== 'passport' && !KYC_KEYS.includes(d.key))

        const availableKycDocs = kycDocs
            .slice()
            .sort((a, b) => getKycPriority(a.key) - getKycPriority(b.key))
            .filter((doc) => {
                const docData = details?.document_paths?.[doc.key]
                return Boolean(imagePreview[doc.key] || docData?.signed_url)
            })

        const kycRepresentative = availableKycDocs[0] || kycDocs.find((d) => d.key === 'driver_kyc')
        const rest = [kycRepresentative, ...others]

        const available = []
        const unavailable = []

        rest.forEach((doc) => {
            const docData = details?.document_paths?.[doc.key]
            const src = imagePreview[doc.key] || docData?.signed_url
            if (src) available.push(doc)
            else unavailable.push(doc)
        })

        return [passport, ...available, ...unavailable]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [details, imagePreview])

    const statConfigs = [
        { icon: faRoute, value: details?.total_trips || 0, label: 'Total Trips', clickable: true },
        { icon: faClock, value: details?.driver?.total_referrals, label: 'Referrals' },
        { icon: faIndianRupeeSign, value: `₹${details?.driver?.total_earnings ?? 0}`, label: 'Earnings' },
        { icon: faBan, value: details?.driver?.total_cancels ?? 0, label: 'Cancels' },
    ]


    const [stats, setStats] = useState([]);
    const [statsLoading, setStatsLoading] = useState(false);
    const statsFetchedRef = useRef(false);

    const handleSwitch = async () => {
        setSwitchCon(switchCon === 'Stats' ? null : 'Stats')

        // Only hit the API the first time Stats is opened for this page visit
        // if (statsFetchedRef.current) return

        // statsFetchedRef.current = true
        // setStatsLoading(true)
        // try {
        //     const fetchData = await axios.get(
        //         `${process.env.REACT_APP_URL}/api/drivers/tripsData/${details?.driver?.cab_id}`,
        //         { withCredentials: true }
        //     )
        //     setStats(groupByMonth(fetchData.data.stats))
        // } catch (error) {
        //     console.log(error, 'error fetching trip stats')
        //     // allow a retry on next click if the request failed
        //     statsFetchedRef.current = false
        // } finally {
        //     setStatsLoading(false)
        // }
    }

    const [selectedReferral, setSelectedReferral] = useState(null)

    const handleSoftDelete = async () => {
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_URL}/api/drivers/softDelete`,
                {},
                {
                    params: { driver_id },
                    withCredentials: true,
                }
            );

            setMessage({
                type: "success",
                message: response.data.message || "Driver hidden successfully.",
            });

        } catch (error) {
            console.error(error);

            setMessage({
                type: "error",
                message: error.response?.data?.message || "Failed to hide driver.",
            });
        }
    };

    return (
        <div className={styles.container}>
            {message && <MessageDisplay message={message} />}

            {/* Header */}
            <div className={styles.heading}>
                <button type="button" className={styles.bbtn} onClick={handleNavigate} aria-label="Go back">
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <div className={styles.headingText}>
                    <span className={styles.eyebrow}>Manifest ID: {driver_id}</span>
                </div>

                {details?.driver?.SoftDelete === 0 ? <button
                    className={styles.handleDelete}
                    title="This action cannot be undone."
                    onClick={handleSoftDelete}
                >
                    Hide Driver
                </button> :
                    <span >  This driver is hidden</span>
                }

            </div>

            <hr className={styles.headerDivider} />

            <div className={styles.subContainer}>
                {/* Left Side: Profile & Stats Container */}
                <div className={styles.top}>
                    {/* Profile Card */}
                    <div className={styles.pcontain}>
                        <div className={styles.verifyControl}>
                            <button
                                type="button"
                                disabled={!isReadWrite || loading}
                                onClick={handleVerification}
                                className={`${styles.verifToggle} ${details?.driver?.verification ? styles.isVerified : styles.isUnverified}`}
                            >
                                <FontAwesomeIcon icon={faShieldHalved} />
                                {verifyLabel}
                            </button>
                        </div>

                        <div className={styles.pfp}>
                            <div className={styles.imgcontainer}>
                                {loading ? (
                                    <div className={`${styles.image} ${styles.skeleton}`} />
                                ) : (
                                    <div className={`${styles.image} ${ringClass}`}>
                                        <img className={styles.img} src={pfp || '/fallback-avatar.jpg'} alt="Driver Avatar" />
                                    </div>
                                )}
                            </div>
                            <div className={styles.pfpdetails}>
                                {loading ? (
                                    <>
                                        <div className={`${styles.skeleton} ${styles.skelName}`} />
                                        <div className={styles.pfpTags}>
                                            <div className={`${styles.skeleton} ${styles.skelTag}`} />
                                            <div className={`${styles.skeleton} ${styles.skelTag}`} />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <h4 className={styles.driverName}>{details?.driver?.driver_name || 'Unnamed Driver'}</h4>
                                        <div className={styles.pfpTags}>
                                            <span className={styles.mediumTag}>
                                                <FontAwesomeIcon icon={faCar} />
                                                {details?.driver?.medium_type || 'N/A'}
                                            </span>
                                            <span className={styles.mediumTag}>
                                                <FontAwesomeIcon icon={faLayerGroup} />
                                                {details?.driver?.service_type || 'N/A'}
                                            </span>
                                            <span className={styles.ratingTag}>
                                                <FontAwesomeIcon icon={faStar} />
                                                {details?.driver?.driver_ratings || '0.0'}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Profile Info Details */}
                        <div className={styles.infoList}>
                            {loading
                                ? infoRows.map((row) => (
                                    <div className={styles.infoRow} key={row.key}>
                                        <div className={`${styles.infoIconBox} ${styles.skeleton}`} />
                                        <div className={styles.infoText} style={{ width: '100%' }}>
                                            <div className={`${styles.skeleton} ${styles.skelLine}`} />
                                        </div>
                                    </div>
                                ))
                                : infoRows.map((row) => (
                                    <div className={styles.infoRow} key={row.key}>
                                        <div className={styles.infoIconBox}>
                                            <FontAwesomeIcon icon={row.icon} />
                                        </div>
                                        <div className={styles.infoText}>
                                            <span>{details?.driver?.[row.key] ?? '—'}</span>
                                            <span>{row.label}</span>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>

                    {/* Stat Manifest Box */}
                    <div className={styles.pp}>
                        {loading
                            ? statConfigs.map((_, i) => (
                                <div className={styles.total} key={i}>
                                    <div className={`${styles.statIcon} ${styles.skeleton}`} />
                                    <div className={styles.statText} style={{ width: '60px' }}>
                                        <div className={`${styles.skeleton} ${styles.skelStatValue}`} />
                                        <div className={`${styles.skeleton} ${styles.skelStatLabel}`} />
                                    </div>
                                </div>
                            ))
                            : statConfigs.map((s, i) => (
                                <div
                                    className={`${styles.total} ${s.clickable ? styles.totalClickable : ''}`}
                                    key={i}
                                    onClick={s.clickable ? handleNav : undefined}
                                >
                                    <div className={`${styles.statIcon} ${styles.statIconRoute}`}>
                                        <FontAwesomeIcon icon={s.icon} />
                                    </div>
                                    <div className={styles.statText}>
                                        <span>{s.value}</span>
                                        <strong>{s.label}</strong>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>

                {/* Right Side: Document / Stats View */}
                <div className={styles.details}>
                    <div className={styles.detailshead}>
                        <h3>
                            <FontAwesomeIcon icon={switchCon === 'Stats' ? faLayerGroup : faFileUpload} />
                            {switchCon === 'Stats' ? 'Stats' : 'Document'}
                        </h3>
                        <button type="button" onClick={() => handleSwitch()}>
                            <FontAwesomeIcon icon={switchCon === 'Stats' ? faFileUpload : faLayerGroup} />
                            {switchCon === 'Stats' ? 'View Documents' : 'Stats View'}
                        </button>
                    </div>

                    {switchCon === 'Stats' ? (
                        <div className={styles.statsContainer}>
                            {statsLoading
                                ? (
                                    <div className={styles.statGrid}>
                                        {statConfigs.map((_, i) => (
                                            <div className={styles.statCard} key={i}>
                                                <div className={`${styles.statCardIcon} ${styles.skeleton}`} />
                                                <div className={styles.statCardText} style={{ width: '80px' }}>
                                                    <div className={`${styles.skeleton} ${styles.skelStatValue}`} />
                                                    <div className={`${styles.skeleton} ${styles.skelStatLabel}`} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )
                                :
                                <div className={styles.barcontainer}>

                                    <div className={styles.statGrid}>
                                        {statConfigs.map((s, i) => (
                                            <div
                                                key={i}
                                                className={`${styles.statCard} ${s.clickable ? styles.statCardClickable : ''}`}
                                                onClick={s.clickable ? handleNav : undefined}
                                            >
                                                <div className={styles.statCardIcon}>
                                                    <FontAwesomeIcon icon={s.icon} />
                                                </div>

                                                <div className={styles.statCardText}>
                                                    <span>{s.value}</span>
                                                    <strong>{s.label}</strong>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className={styles.insight}>

                                        <div className={styles.in1}>
                                            <h3 >OverView</h3>
                                            <Insights data={stats} />
                                        </div>

                                        <div className={styles.in1}>
                                            <h3>
                                                Weekly
                                            </h3>
                                            <Insights data={stats} />
                                        </div>


                                    </div>
                                    <h3 className={styles.sectionLabel}>This Month</h3>
                                    <Bar data={stats} />

                                    {details?.driver?.referred_drivers?.length > 0 && (
                                        <div className={styles.referralSection}>
                                            <h3 className={styles.sectionLabel}>
                                                Referred Drivers ({details.driver.referred_drivers.length})
                                            </h3>
                                            <div className={styles.referralScroll}>
                                                <div className={styles.referralList}>
                                                    <div className={styles.referralHeaderRow}>
                                                        <span>Driver</span>
                                                        <span>Cab</span>
                                                        <span>Total</span>
                                                        <span>Completed</span>
                                                        <span>Missed</span>
                                                    </div>

                                                    {details.driver.referred_drivers.map((r) => {
                                                        const key = `${r.driver_ID}-${r.cab_id}`
                                                        const isActive = selectedReferral === key
                                                        const total = Number(r.completed_count ?? 0) + Number(r.expired_count ?? 0)
                                                        return (
                                                            <div className={styles.referralGroup} key={key}>
                                                                <button
                                                                    type="button"
                                                                    className={`${styles.referralRow} ${isActive ? styles.referralRowActive : ''}`}
                                                                    onClick={() => setSelectedReferral(isActive ? null : key)}
                                                                >
                                                                    <span className={styles.referralName}>{r.driver_name}</span>
                                                                    <span className={styles.referralCabId}>{r.cab_id ?? '—'}</span>
                                                                    <span className={styles.referralTotal}>{total}</span>
                                                                    <span className={`${styles.referralChip} ${styles.referralCompleted}`}>
                                                                        {r.completed_count}
                                                                        <FontAwesomeIcon icon={faCheck} />
                                                                    </span>
                                                                    <span className={`${styles.referralChip} ${styles.referralExpired}`}>
                                                                        {r.expired_count}
                                                                        <FontAwesomeIcon icon={faTimes} />
                                                                    </span>
                                                                </button>

                                                                {isActive && (
                                                                    <div className={styles.referralHistory}>
                                                                        {r.history?.length > 0 ? (
                                                                            <>
                                                                                <div className={styles.historyHeaderRow}>
                                                                                    <span>Sl/no</span>
                                                                                    <span>Sent</span>
                                                                                    {/* <span>Responded</span> */}
                                                                                    <span>Status</span>
                                                                                </div>
                                                                                {r.history.map((h, index) => (
                                                                                    <div className={styles.historyRow} key={h.id}>
                                                                                        <span className={styles.historyReqId}>{index + 1}</span>
                                                                                        <span className={styles.historyCell}>
                                                                                            {h.send_at ? new Date(h.send_at).toLocaleString() : '—'}
                                                                                        </span>
                                                                                      
                                                                                        <span className={`${styles.historyStatus} ${styles[h.status] || ''}`}>
                                                                                            {h.status}
                                                                                        </span>
                                                                                    </div>
                                                                                ))}
                                                                            </>
                                                                        ) : (
                                                                            <span className={styles.noHistory}>No request history for this driver.</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            }
                        </div>
                    ) : (
                        <div className={styles.doc}>
                            {loading
                                ? sortedDocumentTypes.map((doc) => (
                                    <div className={styles.document} key={doc.key}>
                                        <div className={styles.dochead}>
                                            <div className={`${styles.skeleton} ${styles.skelDocLabel}`} />
                                            <div className={`${styles.skeleton} ${styles.skelChip}`} />
                                        </div>
                                        <div className={`${styles.image_Container} ${styles.skeleton}`} />
                                    </div>
                                ))
                                : sortedDocumentTypes.map((doc) => {
                                    const docData = details?.document_paths?.[doc.key]
                                    const status = getStatus(docData?.verification)
                                    const imageSrc = imagePreview[doc.key] || docData?.signed_url

                                    return (
                                        <div className={styles.document} key={doc.key}>
                                            <div className={styles.dochead}>
                                                <label>
                                                    <FontAwesomeIcon icon={doc.icon} />
                                                    <span className={styles.docLabelText}>{getDisplayLabel(doc)}</span>
                                                </label>
                                                <span className={`${styles.statusChip} ${styles[status.className]}`}>
                                                    <FontAwesomeIcon icon={status.icon} />
                                                    {status.label}
                                                </span>
                                            </div>

                                            <div className={styles.image_Container}>
                                                {imageSrc ? (
                                                    isPdf(docData) ? (
                                                        <button
                                                            type="button"
                                                            className={styles.pdfPreview}
                                                            onClick={() => openPreview({ src: imageSrc, isPdf: true, label: getDisplayLabel(doc) })}
                                                        >
                                                            <FontAwesomeIcon icon={faFileUpload} className={styles.pdfIcon} />
                                                            <span>View PDF Document</span>
                                                        </button>
                                                    ) : (
                                                        <img
                                                            className={styles.docImage}
                                                            src={imageSrc}
                                                            alt={getDisplayLabel(doc)}
                                                            onClick={() => openPreview({ src: imageSrc, isPdf: false, label: getDisplayLabel(doc) })}
                                                        />
                                                    )
                                                ) : (
                                                    <span style={{ fontSize: '0.75rem', color: 'var(--neutral)' }}>No File Uploaded</span>
                                                )}
                                            </div>

                                            <div className={styles.dog}>
                                                {docData?.verification === 'Submitted' && (
                                                    <div className={styles.buttonCon}>
                                                        <button
                                                            type="button"
                                                            disabled={!isReadWrite}
                                                            className={`${styles.validation} ${styles.verify} ${!isReadWrite ? styles.disabled : ''}`}
                                                            onClick={() => handleVerificationAction('Verified', doc.key)}
                                                        >
                                                            <FontAwesomeIcon icon={faCheck} />
                                                            Verify
                                                        </button>
                                                        <button
                                                            type="button"
                                                            disabled={!isReadWrite}
                                                            className={`${styles.validation} ${styles.reject} ${!isReadWrite ? styles.disabled : ''}`}
                                                            onClick={() => handleVerificationAction('Rejected', doc.key)}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                            Reject
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                        </div>
                    )}
                </div>
            </div>

            {/* Document Modal Preview */}
            {preview && (
                <div
                    className={`${styles.previewOverlay} ${previewVisible ? styles.previewOverlayVisible : ''}`}
                    onClick={closePreview}
                >
                    <div
                        className={`${styles.previewModal} ${previewVisible ? styles.previewModalVisible : ''}`}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className={styles.previewHeader}>
                            <span>{preview.label}</span>
                            <button type="button" className={styles.previewClose} onClick={closePreview}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className={styles.previewBody}>
                            {preview.isPdf ? (
                                <>
                                    <iframe src={preview.src} className={styles.previewFrame} title={preview.label} />
                                    <a href={preview.src} target="_blank" rel="noopener noreferrer" className={styles.previewFallbackLink}>
                                        <FontAwesomeIcon icon={faUpRightFromSquare} />
                                        Open in new tab
                                    </a>
                                </>
                            ) : (
                                <img src={preview.src} alt={preview.label} className={styles.previewImage} />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default DriverDetails